import { useEffect, useRef, useState } from "react";
import axios from "axios";
import MeetingWaitingRoom from "@/components/MeetingWaitingRoom";
import { API, formatApiError, useAuth } from "@/contexts/AuthContext";

const PAID = new Set(["Starter", "Creator", "Business", "Agency", "Enterprise Basic",
  "Enterprise Plus", "Enterprise Advanced", "Enterprise Elite", "Enterprise Unlimited"]);

function localInput(value) {
  const date = new Date(value);
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function emptyForm() {
  return { kind: "appointment", title: "", description: "", start_at: "", end_at: "", location: "" };
}

function describeDate(event) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: event.timezone, dateStyle: "medium", timeStyle: "short",
    }).format(new Date(event.start_at));
  } catch { return "Event time unavailable"; }
}

export default function MeetingsCalendarPage() {
  const { user } = useAuth();
  const context = user?.workspace_context;
  const plan = context?.subscription_plan || user?.subscription_plan || "Presale";
  const ownWorkspace = !user?.active_workspace || user.active_workspace === user?.id;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [month, setMonth] = useState(() => localInput(new Date()).slice(0, 7));
  const [capabilities, setCapabilities] = useState(null);
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState("");
  const sequence = useRef(0);
  const creationRequest = useRef(null);

  useEffect(() => () => { sequence.current += 1; }, []);

  function changeMonth(value) {
    sequence.current += 1;
    setMonth(value); setEvents([]); setCursor(null); setLoaded(false);
    setBusy(false); setError(""); setNotice("");
  }

  async function loadEvents(append = false) {
    if (!/^\d{4}-\d{2}$/.test(month)) {
      setError("Choose a calendar month."); return;
    }
    const current = ++sequence.current;
    setBusy(true); setError("");
    try {
      const [year, number] = month.split("-").map(Number);
      const params = {
        start: new Date(year, number - 1, 1).toISOString(),
        end: new Date(year, number, 1).toISOString(),
        include_cancelled: true, limit: 100,
        ...(append && cursor ? cursor : {}),
      };
      const { data } = await axios.get(`${API}/meetings/events`, { params });
      if (current !== sequence.current) return;
      if (!Array.isArray(data?.events)) throw new Error("Invalid calendar response.");
      setEvents((previous) => append
        ? [...previous, ...data.events.filter((event) => !previous.some((old) => old.id === event.id))]
        : data.events);
      setCursor(data.next_cursor || null); setLoaded(true);
    } catch (failure) {
      if (current === sequence.current) {
        setError(formatApiError(failure.response?.data?.detail || failure.message));
      }
    } finally {
      if (current === sequence.current) setBusy(false);
    }
  }

  async function openCalendar() {
    if (busy) return;
    const current = ++sequence.current;
    setBusy(true); setError("");
    try {
      const { data } = await axios.get(`${API}/meetings/capabilities`);
      if (current !== sequence.current) return;
      if (data?.calendar_management !== true) throw new Error("Calendar management is unavailable.");
      setCapabilities(data);
      await loadEvents();
    } catch (failure) {
      if (current === sequence.current) {
        setError(formatApiError(failure.response?.data?.detail || failure.message));
        setBusy(false);
      }
    }
  }

  function editEvent(event) {
    setEditing({ id: event.id, revision: event.revision, kind: event.kind });
    setForm({ kind: event.kind, title: event.title, description: event.description || "",
      location: event.location || "", start_at: localInput(event.start_at),
      end_at: localInput(event.end_at) });
    setNotice(""); setError("");
  }

  async function saveEvent(event) {
    event.preventDefault();
    if (saving || busy) return;
    setSaving(true); setError(""); setNotice("");
    try {
      const start = new Date(form.start_at);
      const end = new Date(form.end_at);
      if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) {
        throw new Error("Choose a valid start and a later end time.");
      }
      if (localInput(start) !== form.start_at || localInput(end) !== form.end_at) {
        throw new Error("This local time does not exist because the clocks change. Choose another time.");
      }
      const payload = { ...form, start_at: start.toISOString(), end_at: end.toISOString(),
        timezone, kind: editing?.kind || form.kind };
      if (editing) {
        await axios.put(`${API}/meetings/events/${editing.id}`,
          { ...payload, expected_revision: editing.revision });
      } else {
        creationRequest.current ||= window.crypto.randomUUID();
        await axios.post(`${API}/meetings/events`, { ...payload, request_id: creationRequest.current });
      }
      setNotice(editing ? "Event updated." : "Event added.");
      setEditing(null); setForm(emptyForm()); creationRequest.current = null;
      await loadEvents();
    } catch (failure) {
      setError(formatApiError(failure.response?.data?.detail || failure.message));
    } finally { setSaving(false); }
  }

  async function cancelEvent(event) {
    if (saving || busy || !window.confirm(`Cancel “${event.title}”?`)) return;
    setSaving(true); setError(""); setNotice("");
    try {
      await axios.post(`${API}/meetings/events/${event.id}/cancel`,
        { expected_revision: event.revision });
      if (editing?.id === event.id) { setEditing(null); setForm(emptyForm()); }
      setNotice("Event cancelled.");
      await loadEvents();
    } catch (failure) {
      setError(formatApiError(failure.response?.data?.detail || failure.message));
    } finally { setSaving(false); }
  }

  if (!PAID.has(plan)) {
    return <section className="max-w-xl space-y-3">
      <h1 className="text-2xl font-semibold">Meetings &amp; Calendar</h1>
      <p>Planning and hosting are available from Starter. Kickstart licences can participate as invited guests when guest access is available.</p>
    </section>;
  }
  if (!ownWorkspace) {
    return <section className="max-w-xl space-y-3">
      <h1 className="text-2xl font-semibold">Meetings &amp; Calendar</h1>
      <p>Calendar management is currently available to this company's workspace owner.</p>
    </section>;
  }

  return <section className="max-w-5xl space-y-5" data-testid="meetings-calendar-page">
    <header>
      <h1 className="text-2xl font-semibold">Meetings &amp; Calendar</h1>
      <p className="mt-1 text-sm text-slate-600">Manage your company's appointments.</p>
    </header>
    {error && <p role="alert" className="rounded border border-amber-200 bg-amber-50 p-3 text-sm">{error}</p>}
    {notice && <p role="status" className="text-sm text-emerald-700">{notice}</p>}
    {!capabilities ? <button type="button" onClick={openCalendar} disabled={busy}
      className="zy-btn-primary">{busy ? "Opening…" : "Open calendar"}</button> : <>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">Month
          <input type="month" value={month} onChange={(event) => changeMonth(event.target.value)}
            disabled={saving} className="ml-2 rounded border p-2" />
        </label>
        <button type="button" onClick={() => loadEvents()} disabled={busy || saving}
          className="rounded border px-4 py-2 text-sm">{busy ? "Loading…" : "Load month"}</button>
      </div>
      <p className="text-xs text-slate-600">Video is not available yet. Guest participation remains disabled until activation.</p>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-3">
          {!loaded && <p className="text-sm text-slate-500">Load the selected month to see appointments.</p>}
          {loaded && events.length === 0 && <p className="text-sm text-slate-500">No events in this month.</p>}
          {events.map((event) => <article key={event.id} className="rounded-xl border bg-white p-4 space-y-2">
            <h2 className="font-semibold">{event.title}</h2>
            <p className="text-sm">{describeDate(event)} · {event.timezone}</p>
            {event.location && <p className="text-sm text-slate-600">{event.location}</p>}
            <p className="text-xs uppercase text-slate-500">{event.status}</p>
            {event.status === "planned" && <div className="flex gap-3">
              <button type="button" disabled={saving || busy} onClick={() => editEvent(event)}
                className="rounded border px-3 py-1.5 text-sm">Edit</button>
              <button type="button" disabled={saving || busy} onClick={() => cancelEvent(event)}
                className="rounded border px-3 py-1.5 text-sm">Cancel event</button>
            </div>}
            {event.status === "planned" && event.kind === "meeting" && <MeetingWaitingRoom key={event.id + ":" + event.revision} event={event} />}
          </article>)}
          {cursor && <button type="button" onClick={() => loadEvents(true)} disabled={saving || busy}
            className="rounded border px-4 py-2 text-sm">Load more</button>}
        </div>
        <form onSubmit={saveEvent} className="rounded-xl border bg-white p-5 space-y-4">
          <h2 className="font-semibold">{editing ? "Edit event" : "New event"}</h2>
          <p className="text-xs text-slate-600">Enter times in {timezone}.</p>
          <label className="block text-sm">Event type
            <select value={form.kind} disabled={saving || Boolean(editing)}
              onChange={(event) => setForm((previous) => ({ ...previous, kind: event.target.value }))}
              className="mt-1 block w-full rounded border p-2">
              <option value="appointment">Appointment</option>
              <option value="meeting">Meeting</option>
            </select>
          </label>
          {[
            ["title", "Title", "text", true],
            ["start_at", "Start", "datetime-local", true],
            ["end_at", "End", "datetime-local", true],
            ["location", "Location", "text", false],
          ].map(([key, label, type, required]) => <label key={key} className="block text-sm">
            {label}<input type={type} required={required} value={form[key]} disabled={saving}
              maxLength={key === "title" ? 200 : key === "location" ? 300 : undefined}
              onChange={(event) => setForm((previous) => ({ ...previous, [key]: event.target.value }))}
              className="mt-1 block w-full rounded border p-2" />
          </label>)}
          <label className="block text-sm">Notes
            <textarea value={form.description} maxLength={4000} disabled={saving}
              onChange={(event) => setForm((previous) => ({ ...previous, description: event.target.value }))}
              className="mt-1 block w-full rounded border p-2" />
          </label>
          <div className="flex gap-3">
            <button type="submit" disabled={saving || busy} className="zy-btn-primary">
              {saving ? "Saving…" : editing ? "Save changes" : "Add event"}
            </button>
            <button type="button" disabled={saving}
              onClick={() => {
                if (!editing && creationRequest.current &&
                    !window.confirm("A previous save may have succeeded. Check the calendar before creating a new event. Clear this draft?")) return;
                setEditing(null); setForm(emptyForm()); setError(""); creationRequest.current = null;
              }}
              className="rounded border px-3 py-2 text-sm">{editing ? "Discard edits" : "Clear form"}</button>
          </div>
        </form>
      </div>
    </>}
  </section>;
}
